import { create } from 'zustand';
import { supabase } from '@/lib/supabase/client';
import { Supplier } from '@/types/suppliers';

interface SupplierState {
  suppliers: Supplier[];
  isLoading: boolean;
  error: string | null;
  fetchSuppliers: () => Promise<void>;
  createSupplier: (data: Partial<Supplier>) => Promise<void>;
  updateSupplier: (id: string, data: Partial<Supplier>) => Promise<void>;
  deleteSupplier: (id: string) => Promise<void>;
}

export const useSupplierStore = create<SupplierState>((set, get) => ({
  suppliers: [],
  isLoading: false,
  error: null,

  fetchSuppliers: async () => {
    try {
      set({ isLoading: true, error: null });
      const { data, error } = await supabase
        .from('suppliers')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      set({ suppliers: data || [] });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch suppliers';
      set({ error: message });
      console.error('Error fetching suppliers:', err);
    } finally {
      set({ isLoading: false });
    }
  },

  createSupplier: async (data) => {
    try {
      set({ isLoading: true, error: null });

      // Ensure bank_details is properly formatted as JSONB
      const formattedData = {
        ...data,
        bank_details: data.bank_details ? JSON.stringify(data.bank_details) : null,
        addresses: data.addresses ? JSON.stringify(data.addresses) : null
      };

      const { error } = await supabase
        .from('suppliers')
        .insert([formattedData]);

      if (error) throw error;

      // Refresh suppliers list
      await get().fetchSuppliers();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create supplier';
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  updateSupplier: async (id, data) => {
    try {
      set({ isLoading: true, error: null });

      // Ensure bank_details is properly formatted as JSONB
      const formattedData = {
        ...data,
        bank_details: data.bank_details ? JSON.stringify(data.bank_details) : null,
        addresses: data.addresses ? JSON.stringify(data.addresses) : null,
        updated_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('suppliers')
        .update(formattedData)
        .eq('id', id);

      if (error) throw error;

      // Refresh suppliers list
      await get().fetchSuppliers();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update supplier';
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  deleteSupplier: async (id) => {
    try {
      set({ isLoading: true, error: null });
      const { error } = await supabase
        .from('suppliers')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Refresh suppliers list
      await get().fetchSuppliers();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete supplier';
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  }
}));

// Set up realtime subscription
supabase
  .channel('suppliers_changes')
  .on(
    'postgres_changes',
    { event: '*', schema: 'public', table: 'suppliers' },
    () => {
      useSupplierStore.getState().fetchSuppliers();
    }
  )
  .subscribe();